import { AdaptationCycleStageConfig } from '../types'
import { Landmark, Cog, Handshake } from 'lucide-react'
import adaptationCycleStageTwoImage from '@app/assets/illustrations/adaptation-cycle-overview-2.png'

export const adaptationCycleStageTwo: AdaptationCycleStageConfig = {
  id: 'stage-two',
  title: 'Stage 2: Prioritisation',
  description:
    'Stage 2 seeks to develop a shared understanding of hazards, what is exposed to them, and what communities and organisations value, and uses that understanding to identify where detailed adaptation planning is most needed.',
  tagline: '',
  heroImage: adaptationCycleStageTwoImage,

  outcome: {
    title: 'Outcome of Stage 2',
    description:
      'A prioritised set of focus areas grounded in evidence and supported by those who will be involved in subsequent decisions. This is documented in the Stage 2 Report: Broad-Scale Risk Assessment and Prioritisation, which marks the transition from broad-scale screening to the detailed planning work of Stage 3.',
  },
  topParagraphs: [
    'The central challenge of this stage is that technical and values-based understanding need to inform each other rather than run in parallel. What makes something a risk is not the hazard alone, but the hazard in relation to what people and organisations care about. Activities 2.1 and 2.2 are designed to run concurrently for this reason. Prioritisation in 2.3 then requires bringing those streams together in a way that is transparent and defensible. Weighting criteria are often contested, and the guidance in 2.3 addresses this directly.',
    'The three activities are mutually reinforcing rather than strictly sequential. Activities 2.1 and 2.2 in particular are designed to run concurrently, with technical and values-based understanding informing each other throughout.',
    'Focus area: the location, community, asset system, or network selected for detailed adaptation planning. In statutory territorial-authority planning, a focus area may comprise all or part of a priority location identified through a regional spatial plan..',
  ],
  activities: [
    {
      activity: 'Activity 2.1',
      forumSlug: 'broad-scale-risk-screening',
      title: 'Broad-Scale Risk Assessment',
      mfeAlignment: [
        { step: 2, label: 'Assess sea-level rise and coastal hazards' },
        { step: 4, label: 'Assess vulnerability and risk' },
      ],
      description:
        'Building on the baseline stocktake completed in Activity 1.3, this activity evaluates available hazard and element data to produce a broad-scale picture of where hazards intersect with people, assets, and what communities value. The aim is an assessment that is sufficient for screening and prioritisation. Data gaps are expected and should be documented and carried forward rather than resolved before proceeding. Waiting for complete datasets is a common source of delay and rarely changes the direction of conclusions.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Confirm that analysis scope aligns with the mandate and decision needs established in Stage 1.',
            'Confirm that TAG review and documentation requirements are in place to ensure outputs meet agreed standards of transparency and reproducibility.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Contribute datasets and local knowledge to ensure the assessment draws on the best available information.',
            'Review the historical impacts storyline for accuracy and completeness before use in engagement.',
            'Prepare outputs in accessible formats for governance and partnership review.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Evaluate existing hazard and element datasets (population, assets, infrastructure, land uses) for quality, coverage, and fitness for purpose.',
            'Identify and document data gaps.',
            'Fill critical gaps where practical and carry others forward with appropriate caveats.',
            'Conduct an exposure assessment to quantify intersections of hazards and elements under relevant climate scenarios and timeframes.',
            'Estimate broad consequences using indicators appropriate for broad-scale screening.',
            'Develop a storyline of past hazard impacts drawing on historical event data collated in 1.3, framed to support later community engagement.',
          ],
        },
      ],
      outputs: [
        {
          // TODO: link to the Data + Evidence tab, not the forum - destination route not yet known
          text: 'Inventory of elements (population, assets, infrastructure, land uses).',
          link: {
            text: 'Link to Data + Evidence tab →',
            href: 'https://forum.resilience-explorer.com/t/adaptation-cycle-activity-2-1-examples-broad-scale-risk-screening/76/1',
          },
        },
        {
          text: 'Exposure assessment: quantified intersections of hazards and elements under climate scenarios.',
        },
        {
          text: 'Broad-scale risk characterisation, covering physical exposure and damage (including damage and isolation), social exposure and vulnerability indicators (demographic characteristics, deprivation, access to resources), and economic exposure and vulnerability indicators (land uses, key sectors, supply chains, industries).',
        },
        {
          text: 'Historical impacts storyline.',
        },
      ],
      connections: [
        'Builds on baseline datasets established in 1.3. The guiding principles from 1.3 inform how the assessment is conducted, including which knowledge systems are recognised, how data sovereignty applies, and what transparency standards apply to outputs.',
        'Provides the technical foundation for values and concerns (2.2), prioritisation (2.3), and subsequent detailed risk assessments (3.2).',
        'Supports the identification of indicators and data structures for signal and trigger development (3.5) and monitoring (4.2).',
      ],
      relatedActivities: [
        {
          stageId: 'stage-two',
          activity: 'Activity 2.2',
          label: '2.2',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.3',
          label: '2.3',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.5',
          label: '3.5',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
      ],
      moveOnWhen: [
        'Hazard and element datasets are sufficient for a first-pass assessment, quality-checked, and endorsed by technical leads.',
        'Exposure assessment and broad consequences metrics are documented.',
        'Historical impacts storyline is prepared.',
        'Data gaps are documented with a clear note on how they will be managed going forward.',
      ],
      resources: [
        {
          title: 'Risk and Consequences',
          articles: [
            {
              title: 'What Is Risk',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
            },
            {
              title: 'What Are Consequences',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
            },
            {
              title: 'What Is Exposure',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-exposure',
            },
            {
              title: 'Criticality and Importance',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-criticality',
            },
            {
              title: 'Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/vulnerability',
            },
            {
              title: 'Social Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/social-vulnerability',
            },
          ],
        },
        {
          title: 'Hazards',
          articles: [
            {
              title: 'Hazards and Environmental Change',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/hazards-and-environmental-change',
            },
            {
              title: 'Understanding Return Periods',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/understanding-return-periods-and-ae-ps',
            },
            {
              title: 'Cascading Hazards',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/cascading-hazards',
            },
            {
              title: 'Sea Level Rise',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/sea-level-rise',
            },
            {
              title: 'Rising Groundwater',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/rising-groundwater',
            },
            {
              title: 'Coastal Flooding (Inundation)',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/coastal-flooding-inundation',
            },
            {
              title: 'River (Fluvial) Flooding',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/river-fluvial-flooding',
            },
            {
              title: 'Coastal Erosion',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/coastal-erosion',
            },
            {
              title: 'Landslides',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/landslides',
            },
          ],
        },
        {
          title: 'Climate Scenarios and Uncertainty',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
            {
              title: 'Global Warming Levels',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/global-warming-levels-gw-ls',
            },
            {
              title: 'Sources of Uncertainty in Modelling',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
            },
            {
              title: 'Strength of Knowledge',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 2.2',
      forumSlug: 'values-and-concern-assessment',
      title: 'Values and Concern Assessment',
      mfeAlignment: [
        { step: 3, label: 'Establish values and objectives' },
        { step: 4, label: 'Assess vulnerability and risk' },
      ],
      description:
        'This activity identifies what people, communities, and organisations value and how they perceive and experience risk. Physical processes only become hazards when they threaten what people care about, which is why this activity runs concurrently with 2.1. Together they form the basis for prioritisation in 2.3. Engagement at this stage is broad but light-touch, at ward, community board, or representative group level, with deeper local engagement following in Activity 3.2. A note on terminology: "concern assessment" follows the IRGC Risk Governance Framework, and is distinct from social vulnerability assessment, which characterises population susceptibility to harm and sits in Activity 2.1 — concern assessment instead focuses on what people value, how they perceive risk, and what matters to them about hazard impacts.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Confirm that the process for identifying and recording what people value is consistent with organisational principles and Treaty obligations, particularly in how tangata whenua perspectives are sought and recorded.',
            'Document organisational obligations (levels of service, legal requirements, Treaty commitments) as a baseline against which community concerns can be understood.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Engage iwi, mana whenua, community boards, NGOs, asset managers, and sector representatives to contribute values and concerns at a broad scale.',
            'Ensure tangata whenua perspectives are recorded respectfully, following the protocols agreed in 1.2 and documented in the Indigenous Knowledge Management Plan from 1.3.',
            'Identify values and concerns that will require deeper exploration in the focus area in Stage 3.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Translate hazard and exposure outputs from 2.1 into accessible formats for engagement: maps, summaries, and scenario illustrations that make technical findings meaningful to non-specialists.',
            'Design and conduct a broad-scale concern assessment using workshops, surveys, or structured discussions.',
            'Use the risk tolerance factors (voluntariness, fairness, place attachment, controllability, catastrophic potential, equity) as a structured framework for what to surface.',
            'Compile a consistent record of what is of value, concerns, and perceptions, noting where technical assessments and community concerns emphasise different aspects of risk.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Record of values and concerns at a broad scale, covering organisational obligations, broad-scale priorities, and community concerns.',
        },
        {
          text: 'Accessible materials linking hazards to broad concerns.',
        },
        {
          text: 'Documentation of where technical assessments and community concerns emphasise different aspects of risk.',
        },
      ],
      connections: [
        'Runs concurrently with 2.1. Technical and values-based understanding should inform each other throughout.',
        'Builds on stakeholder relationships and engagement principles from 1.1 and 1.2.',
        'Provides the social and institutional context for prioritisation (2.3), detailed risk assessment (3.2), and options evaluation (3.4).',
        'Establishes the foundation for defining community risk tolerance thresholds in Activity 3.2.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-two',
          activity: 'Activity 2.1',
          label: '2.1',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.3',
          label: '2.3',
        },
        { stageId: 'stage-one', activity: 'Activity 1.1', label: '1.1' },
        { stageId: 'stage-one', activity: 'Activity 1.2', label: '1.2' },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.4',
          label: '3.4',
        },
      ],
      moveOnWhen: [
        'Broad-scale values and concerns are documented with input from all three streams.',
        'Organisational obligations are articulated alongside community concerns.',
        'Key partners consider the process to have been transparent and inclusive.',
        'Areas requiring deeper focus-area values work in Stage 3 are identified.',
      ],
      resources: [
        {
          title: 'Community Engagement',
          articles: [
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
          ],
        },
        {
          title: 'Values and Worldviews',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
        {
          title: 'Vulnerability and Community Wellbeing',
          articles: [
            {
              title: 'Why Climate Adaptation Needs a Systems View of Wellbeing',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/why-climate-adaptation-needs-a-systems-view-of-wellbeing',
            },
            {
              title: 'Social Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/social-vulnerability',
            },
            {
              title: 'Why Social Vulnerability Indicators Tell Only Half The Story',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/why-social-vulnerability-indicators-tell-only-half-the-story',
            },
          ],
        },
        {
          title: 'Māori Perspectives',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Mātauranga Māori (Māori Knowledge)',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/matauranga-maori-maori-knowledge',
            },
            {
              title: 'Protection of Māori Rights',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/protection-of-maori-rights',
            },
            {
              title: 'Marae-opoly: A Serious Game to Support Adaptation Decision-Making',
              description: '',
              href: '/app/guide/insights/research-and-methods/marae-opoly-a-serious-game-to-support-adaptation-decision-making',
            },
          ],
        },
        {
          title: 'Risk Perception and Behaviour',
          articles: [
            {
              title: 'Foundations of Risk Perception',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-perception',
            },
            {
              title: 'Heuristics and Cognitive Biases in Risk Decisions',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
            },
            {
              title: 'Framing Effects in Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/framing-effects-in-risk-communication',
            },
            {
              title: 'Countering Science Denial',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/countering-science-denial',
            },
            {
              title: 'Foundations of Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-communication',
            },
          ],
        },
        {
          title: 'Further Reading and External Guidance',
          articles: [
            {
              title: 'Serious Games',
              description: '',
              href: 'https://niwa.co.nz/social-science/serious-games',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 2.3',
      forumSlug: 'prioritisation-and-focus-area-site-selection',
      title: 'Prioritise Focus Areas',
      mfeAlignment: [{ step: 4, label: 'Assess vulnerability and risk' }],
      description:
        'This activity brings together the technical assessment (2.1) and the concern assessment (2.2) to identify priority areas and/or sites for detailed adaptation planning in Stage 3. It balances hazard intensity and likelihood, exposed elements, and broad consequence indicators with community values, organisational obligations, and readiness to engage.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Endorse the process for integrating values and concerns into risk prioritisation.',
            'Agree the criteria and basis for prioritisation, including how technical and community inputs are balanced.',
            'Approve the resulting list of priority areas as the basis for Stage 3.',
            'Confirm that the rationale and process are documented for public accountability.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Contribute perspectives on community readiness, inter-community relationships, and contextual knowledge.',
            'Identify existing areas of agreement and disagreement within the community, including unrelated local tensions or conflicts, so engagement is not derailed by walking unprepared into them.',
            'Sense-check draft priority areas with iwi partners, community boards, and key stakeholders.',
            'Support legitimacy by ensuring affected groups are involved in reviewing and refining area selection.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Develop a prioritisation framework combining hazard and exposure information, consequence indicators, organisational obligations, and community concerns.',
            'Prepare draft maps and summaries of potential priority areas and proposed sequencing.',
            'Integrate readiness to engage and contextual factors such as capacity, community networks, and past engagement fatigue.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Prioritisation framework (criteria, basis for weighting, and balance of technical and community inputs).',
        },
        {
          text: 'Broad-scale summary of priority areas selected for detailed adaptation planning.',
        },
        {
          text: 'Sequencing plan outlining the order in which areas will be addressed.',
        },
        {
          text: 'Stage 2 Report: Broad-Scale Risk Assessment and Prioritisation, combining outputs of 2.1, 2.2, and 2.3.',
        },
      ],
      connections: [
        'Integrates hazard information (2.1) and values and concerns (2.2) to identify priority areas for Stage 3.',
        'Links back to governance scope and mandate (1.1) to ensure decisions align with organisational authority.',
        'Provides sequencing and readiness insights for implementation planning (4.1).',
      ],
      relatedActivities: [
        {
          stageId: 'stage-two',
          activity: 'Activity 2.1',
          label: '2.1',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.2',
          label: '2.2',
        },
        { stageId: 'stage-one', activity: 'Activity 1.1', label: '1.1' },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.1',
          label: '4.1',
        },
      ],
      moveOnWhen: [
        'Priority areas identified and endorsed by governance and key partners.',
        'Sequencing plan agreed and communicated.',
        'Rationale for prioritisation documented for public accountability.',
      ],
      resources: [
        {
          title: 'Prioritisation and Scoring',
          articles: [
            {
              title: 'The Planning Process for Risk',
              description: '',
              href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
            },
            {
              title: 'Criticality and Importance',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-criticality',
            },
            {
              title: 'Social Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/social-vulnerability',
            },
          ],
        },
        {
          title: 'Decision-Making under Uncertainty',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
          ],
        },
        {
          title: 'Values and Community',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
      ],
    },
  ],
  previousStage: 'stage-one',
  nextStage: 'stage-three',
}
